import {
    Banner,
    BlockStack,
    Button,
    Card,
    Checkbox,
    InlineStack,
    Layout,
    Page,
    Pagination,
    Select,
    SkeletonBodyText,
    Text,
    TextField,
} from '@shopify/polaris';
import { useCallback, useEffect, useMemo, useState } from 'react';
import agent from '../../app/api/agent';
import { getApiErrorMessage } from '../../app/api/error';
import { LegacyProductMappingPage, LegacyProductMappingRow, LegacyVehicleLevel } from '../../app/models/LegacyMode';

const toOptions = (data?: LegacyVehicleLevel) => [
    { label: 'Select…', value: '' },
    ...(data?.rows.map(row => ({ label: row.value, value: row.value })) ?? []),
];

const LegacyProductMappingsPage = () => {
    const [makes, setMakes] = useState<LegacyVehicleLevel>();
    const [models, setModels] = useState<LegacyVehicleLevel>();
    const [submodels, setSubmodels] = useState<LegacyVehicleLevel>();
    const [years, setYears] = useState<LegacyVehicleLevel>();
    const [make, setMake] = useState('');
    const [model, setModel] = useState('');
    const [submodel, setSubmodel] = useState('');
    const [year, setYear] = useState('');
    const [endYear, setEndYear] = useState('');
    const [query, setQuery] = useState('');
    const [appliedQuery, setAppliedQuery] = useState('');
    const [mappedOnly, setMappedOnly] = useState(false);
    const [page, setPage] = useState(1);
    const [products, setProducts] = useState<LegacyProductMappingPage>();
    const [rows, setRows] = useState<LegacyProductMappingRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [saving, setSaving] = useState(false);
    const [notice, setNotice] = useState<{ tone: 'success' | 'critical'; text: string }>();

    useEffect(() => {
        agent.LegacyAdmin.vehicles({ level: 'make' })
            .then(setMakes)
            .catch(async error => setNotice({ tone: 'critical', text: await getApiErrorMessage(error, 'Unable to load makes.') }))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        setModel(''); setSubmodel(''); setYear(''); setModels(undefined); setSubmodels(undefined); setYears(undefined); setProducts(undefined);
        if (make) agent.LegacyAdmin.vehicles({ level: 'model', make }).then(setModels);
    }, [make]);

    useEffect(() => {
        setSubmodel(''); setYear(''); setSubmodels(undefined); setYears(undefined); setProducts(undefined);
        if (!make || !model) return;
        if (makes?.submodelEnabled) {
            agent.LegacyAdmin.vehicles({ level: 'submodel', make, model }).then(setSubmodels);
        } else {
            agent.LegacyAdmin.vehicles({ level: 'year', make, model }).then(setYears);
        }
    }, [make, model, makes?.submodelEnabled]);

    useEffect(() => {
        setYear(''); setYears(undefined); setProducts(undefined);
        if (make && model && submodel) {
            agent.LegacyAdmin.vehicles({ level: 'year', make, model, submodel }).then(setYears);
        }
    }, [make, model, submodel]);

    const loadProducts = useCallback(async () => {
        if (!make || !model || !year) {
            setProducts(undefined);
            setRows([]);
            return;
        }
        setLoadingProducts(true);
        setNotice(undefined);
        try {
            const result = await agent.LegacyAdmin.mappingProducts({
                make,
                model,
                submodel: submodel || undefined,
                year: Number(year),
                query: appliedQuery,
                mappedOnly,
                page,
                pageSize: 50,
            });
            setProducts(result);
            setRows(result.rows);
        } catch (error) {
            setNotice({ tone: 'critical', text: await getApiErrorMessage(error, 'Unable to load products.') });
        } finally {
            setLoadingProducts(false);
        }
    }, [make, model, submodel, year, appliedQuery, mappedOnly, page]);

    useEffect(() => { loadProducts(); }, [loadProducts]);

    const allSelected = rows.length > 0 && rows.every(row => row.mapped);
    const hasNext = useMemo(() => products ? products.page * products.pageSize < products.total : false, [products]);

    const save = async () => {
        if (!make || !model || !year) return;
        setSaving(true);
        setNotice(undefined);
        try {
            const result = await agent.LegacyAdmin.saveMappings({
                make,
                model,
                submodel: submodel || undefined,
                year: Number(year),
                endYear: endYear ? Number(endYear) : undefined,
                rows,
            });
            setNotice({ tone: 'success', text: `Updated ${result.updatedProductCount} product${result.updatedProductCount === 1 ? '' : 's'}.` });
            await loadProducts();
        } catch (error) {
            setNotice({ tone: 'critical', text: await getApiErrorMessage(error, 'Unable to save product fitments.') });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Page title="Product fitments"><Card><SkeletonBodyText lines={8} /></Card></Page>;

    return (
        <Page title="Product fitments" subtitle="Manually manage exact legacy vehicle tags on Shopify products.">
            <Layout>
                {notice && <Layout.Section><Banner tone={notice.tone}><p>{notice.text}</p></Banner></Layout.Section>}
                <Layout.Section>
                    <Card>
                        <BlockStack gap="400">
                            <Text as="h2" variant="headingMd">Select vehicle</Text>
                            <InlineStack gap="300" wrap>
                                <div style={{ minWidth: 220, flex: 1 }}><Select label="Make" options={toOptions(makes)} value={make} onChange={setMake} /></div>
                                <div style={{ minWidth: 220, flex: 1 }}><Select label="Model" options={toOptions(models)} value={model} onChange={setModel} disabled={!make} /></div>
                                {makes?.submodelEnabled && <div style={{ minWidth: 220, flex: 1 }}><Select label="Submodel" options={toOptions(submodels)} value={submodel} onChange={setSubmodel} disabled={!model} /></div>}
                                <div style={{ minWidth: 180, flex: 1 }}><Select label="Year" options={toOptions(years)} value={year} onChange={value => { setYear(value); setPage(1); }} disabled={!model || (makes?.submodelEnabled && !submodel)} /></div>
                                <div style={{ minWidth: 180, flex: 1 }}><TextField label="To range (optional)" type="number" value={endYear} onChange={setEndYear} autoComplete="off" /></div>
                            </InlineStack>
                            {products?.selectedTag && <Text as="p" tone="subdued">Selected Shopify tag: {products.selectedTag}</Text>}
                            <InlineStack align="start"><Button variant="primary" onClick={save} loading={saving} disabled={!year || rows.length === 0}>Save assignment</Button></InlineStack>
                        </BlockStack>
                    </Card>
                </Layout.Section>

                <Layout.Section>
                    <Card>
                        <BlockStack gap="400">
                            <Text as="h2" variant="headingMd">Select products</Text>
                            <Checkbox label="Only show products mapped to the selected vehicle" checked={mappedOnly} onChange={value => { setMappedOnly(value); setPage(1); }} />
                            <InlineStack gap="200" blockAlign="end" wrap={false}>
                                <div style={{ flex: 1 }}><TextField label="Search products" value={query} onChange={setQuery} autoComplete="off" placeholder="Product, SKU, or vendor" /></div>
                                <Button onClick={() => { setAppliedQuery(query.trim()); setPage(1); }}>Search</Button>
                            </InlineStack>

                            {!year ? <Text as="p" tone="subdued">Select a vehicle to view products.</Text> : loadingProducts ? <SkeletonBodyText lines={8} /> : (
                                <BlockStack gap="200">
                                    <div style={{ borderBottom: '1px solid #dfe3e8', paddingBottom: 12 }}>
                                        <InlineStack gap="300" blockAlign="center" wrap={false}>
                                            <Checkbox label="Select all visible products" labelHidden checked={allSelected} onChange={value => setRows(current => current.map(row => ({ ...row, mapped: value })))} />
                                            <div style={{ flex: 2 }}><Text as="span" fontWeight="semibold">Product</Text></div>
                                            <div style={{ flex: 1 }}><Text as="span" fontWeight="semibold">SKU</Text></div>
                                            <div style={{ flex: 1 }}><Text as="span" fontWeight="semibold">Vendor</Text></div>
                                        </InlineStack>
                                    </div>
                                    {rows.map(row => (
                                        <div key={row.productId} style={{ borderBottom: '1px solid #edf0f2', padding: '12px 0', background: row.mapped ? '#f1f8ff' : undefined }}>
                                            <InlineStack gap="300" blockAlign="center" wrap={false}>
                                                <Checkbox label={`Map ${row.title}`} labelHidden checked={row.mapped} onChange={value => setRows(current => current.map(item => item.productId === row.productId ? { ...item, mapped: value } : item))} />
                                                <div style={{ flex: 2 }}><Text as="span">{row.title}</Text></div>
                                                <div style={{ flex: 1 }}><Text as="span" tone="subdued">{row.sku || '—'}</Text></div>
                                                <div style={{ flex: 1 }}><Text as="span" tone="subdued">{row.vendor || '—'}</Text></div>
                                            </InlineStack>
                                        </div>
                                    ))}
                                    {rows.length === 0 && <Text as="p" tone="subdued">No matching products were found.</Text>}
                                    {products && products.total > products.pageSize && (
                                        <InlineStack align="center">
                                            <Pagination
                                                hasPrevious={page > 1}
                                                onPrevious={() => setPage(current => Math.max(1, current - 1))}
                                                hasNext={hasNext}
                                                onNext={() => setPage(current => current + 1)}
                                            />
                                        </InlineStack>
                                    )}
                                </BlockStack>
                            )}
                        </BlockStack>
                    </Card>
                </Layout.Section>
            </Layout>
        </Page>
    );
};

export default LegacyProductMappingsPage;
